import { ShieldCheck, ShieldX } from "@tamagui/lucide-icons";
import { Auth, useTOTP } from "common";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Heading, Input, Spinner, Text, XStack, YStack } from "tamagui";
import { GradientButton, OutlinedButton } from "tamagui-config";

import ConfirmDialog from "../../components/ConfirmDialog";
import { useShowToast } from "../../hooks/useShowToast";

// TOTP only. The /auth/totp/verify Lambda flips the Cognito MFA preference
// on success, so the client never sets it; disabling goes through Amplify
// directly. SMS MFA is not offered: it needs a verified phone on the account
// and the v6 Auth wrapper has no way to prefer it.
export const Security = () => {
  const { t } = useTranslation();
  const { setupTOTP, verifyTOTP } = useTOTP();
  const { showSuccessToast, showErrorToast } = useShowToast();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);

  const refresh = async () =>
    setEnabled((await Auth.fetchMFAStatus()) === "TOTP");

  useEffect(() => {
    refresh();
  }, []);

  const run = async (work: () => Promise<void>, successKey?: string) => {
    setBusy(true);
    try {
      await work();
      if (successKey) showSuccessToast(t(successKey));
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const onStart = () =>
    run(async () => {
      const result = await setupTOTP();
      setQrCodeUrl(result.qrCodeUrl);
      setCode("");
    });

  const onVerify = () =>
    run(async () => {
      const result = await verifyTOTP(code.trim());
      if (result.status !== "SUCCESS") throw new Error(result.message);
      setQrCodeUrl(null);
      setCode("");
      await refresh();
    }, "security.enabledToast");

  const onDisable = () =>
    run(async () => {
      await Auth.disableTOTP();
      await refresh();
    }, "security.disabledToast");

  return (
    <YStack gap="$4">
      <Heading size="$14" fontSize="$14" $xs={{ fontSize: "$12" }}>
        {t("sidebar.security")}
      </Heading>

      <YStack
        gap="$4"
        backgroundColor="$transparentBorder"
        padding="$4"
        borderRadius="$6"
        maxWidth={760}
      >
        <XStack alignItems="center" justifyContent="space-between" gap="$3">
          <YStack gap="$1.5" flex={1}>
            <Text fontSize="$6" fontWeight="bold">
              {t("security.totp.title")}
            </Text>
            <Text color="$white10">{t("security.totp.description")}</Text>
          </YStack>
          {enabled === null ? (
            <Spinner />
          ) : enabled ? (
            <XStack alignItems="center" gap="$1.5">
              <ShieldCheck color="$successGreen" />
              <Text color="$successGreen" fontWeight="bold">
                {t("security.totp.enabled")}
              </Text>
            </XStack>
          ) : (
            <XStack alignItems="center" gap="$1.5">
              <ShieldX color="$gray10" />
              <Text color="$gray10" fontWeight="bold">
                {t("security.totp.disabled")}
              </Text>
            </XStack>
          )}
        </XStack>

        {enabled === true && (
          <XStack>
            <OutlinedButton
              rounded
              disabled={busy}
              onPress={() => setConfirmDisable(true)}
            >
              {t("security.totp.disableButton")}
            </OutlinedButton>
          </XStack>
        )}

        {enabled === false && !qrCodeUrl && (
          <XStack>
            <GradientButton rounded loading={busy} onPress={onStart}>
              {t("security.totp.setupButton")}
            </GradientButton>
          </XStack>
        )}

        {enabled === false && qrCodeUrl && (
          <XStack gap="$6" alignItems="center" flexWrap="wrap">
            <XStack backgroundColor="white" padding="$3" borderRadius="$5">
              <QRCodeSVG value={qrCodeUrl} size={200} />
            </XStack>
            <YStack gap="$3.5" flex={1} minWidth={280}>
              <Text>{t("security.totp.scan")}</Text>
              <Text color="$white10">{t("security.totp.enterCode")}</Text>
              <Input
                value={code}
                onChangeText={(value) =>
                  setCode(value.replace(/\D/g, "").slice(0, 6))
                }
                inputMode="numeric"
                maxLength={6}
                placeholder={t("security.totp.codePlaceholder")}
              />
              <XStack gap="$3">
                <OutlinedButton
                  rounded
                  disabled={busy}
                  onPress={() => {
                    setQrCodeUrl(null);
                    setCode("");
                  }}
                >
                  {t("common.cancel")}
                </OutlinedButton>
                <GradientButton
                  rounded
                  loading={busy}
                  disabled={code.length !== 6}
                  onPress={onVerify}
                >
                  {t("security.totp.verifyButton")}
                </GradientButton>
              </XStack>
            </YStack>
          </XStack>
        )}
      </YStack>

      <ConfirmDialog
        open={confirmDisable}
        onOpenChange={setConfirmDisable}
        onConfirm={onDisable}
        title={t("security.totp.disableDialog.title")}
        description={t("security.totp.disableDialog.description")}
        confirmButtonText={t("security.totp.disableDialog.confirmButton")}
      />
    </YStack>
  );
};

export default Security;
